import { subject } from '@casl/ability'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardSubheading,
  Text
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { type IncidentReport, IncidentStatus } from '@lmc/ts-common'
import {
  NavigationModal as LeavePrompt,
  PageHeader,
  usePrompt
} from '@lmc/ui-kit'
import type { IncidentCaseV2Step } from '@lmc/validations'
import * as Sentry from '@sentry/react'
import { ConfigProvider, Tooltip } from 'antd'
import { isApiError } from 'common/services/apiError'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import CloseIncidentModal from '../modals/CloseIncidentModal'
import IncidentCaseV2Stepper from './IncidentCaseV2Stepper'
import {
  INCIDENT_CASE_V2_STEPS,
  type IncidentCaseV2StepHandle,
  TOTAL_INCIDENT_CASE_V2_STEPS
} from './incidentCaseV2Steps'
import LogUpdateConfirm from './LogUpdateConfirm'
import ServiceUserDetailsCard from './ServiceUserDetailsCard'
import { CaseCardTitle, STEP_COLLAPSE, stepAntdTheme } from './stepLayout'
import useSaveIncidentCaseV2Step from './useSaveIncidentCaseV2Step'

type IncidentCaseV2Props = {
  incidentReport: IncidentReport
  onExit: () => void
  testId?: string
}

/** The API's code for a save that would leave stored files without an owner. */
const ATTACHMENTS_STILL_STORED = 1042

/**
 * The save was refused because what it removes still has files: an action,
 * or the written follow-up. Nest puts the code in `error`, Express in `code`.
 */
const isAttachmentsStillStored = (error: unknown) => {
  if (!isApiError(error) || error.status !== 409) {
    return false
  }
  const body = error.responseBody as { code?: unknown; error?: unknown }
  return (
    body?.error === ATTACHMENTS_STILL_STORED ||
    body?.code === ATTACHMENTS_STILL_STORED
  )
}

/**
 * Where a returning case lands: the step after the furthest completed one, or
 * the first step once every step is completed, as editing a finished case is.
 */
const landingStepIndex = (
  completedSteps: ReadonlyArray<IncidentCaseV2Step>
) => {
  const furthestCompleted = INCIDENT_CASE_V2_STEPS.reduce(
    (furthest, step, index) =>
      completedSteps.includes(step.id) ? index : furthest,
    -1
  )

  return furthestCompleted === TOTAL_INCIDENT_CASE_V2_STEPS - 1
    ? 0
    : furthestCompleted + 1
}

/**
 * The v2 incident case form's shell: header, step card, Back/Next, stepper and
 * service user card. Testids are `incident-case-v2-*`, apart from the legacy
 * page's, so the legacy Playwright suite can't drive this form by mistake.
 */
const IncidentCaseV2 = ({
  incidentReport,
  onExit,
  testId = 'incident-case-v2'
}: IncidentCaseV2Props) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { t: tGeneral } = useTranslation(NAMESPACE.GENERAL)
  const { saveStep } = useSaveIncidentCaseV2Step({
    incidentLogId: incidentReport.log.id,
    incidentReportId: incidentReport.id
  })
  const handleRef = useRef<IncidentCaseV2StepHandle | null>(null)
  const isSubmitting = useRef(false)
  // Back, the stepper and Close incident wait too, so the save can't land on
  // a different step from the one it was sent for.
  const [isBusy, setIsBusy] = useState(false)

  // The mobile questionnaire's draft is not an opened case: the first save
  // still opens it.
  const savedCase = incidentReport.case?.isDraft ? null : incidentReport.case
  const [isCaseOpen, setIsCaseOpen] = useState(Boolean(savedCase))
  // Fixed on arrival: the first save opening the case doesn't make it an edit.
  const [isResuming] = useState(Boolean(savedCase))
  // Only a case that existed before this visit can be closed from the form.
  const isEditingCase =
    isResuming && incidentReport.status !== IncidentStatus.closed
  const [isClosingIncident, setIsClosingIncident] = useState(false)
  // As on the legacy edit screen: closing needs `delete`, not just `update`.
  const canCloseIncident = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', { log: { item: incidentReport.log.item?.id } })
    }
  ])
  // Asked once a visit, the first time a save would change the log itself.
  const [answerLogUpdate, setAnswerLogUpdate] = useState<
    ((isConfirmed: boolean) => void) | null
  >(null)
  const hasConfirmedLogUpdate = useRef(false)
  const closeNotPermittedId = useId()
  const [completedSteps, setCompletedSteps] = useState<
    ReadonlyArray<IncidentCaseV2Step>
  >(
    // `completedSteps` is not on the ts-common case type.
    () =>
      (savedCase as { completedSteps?: Array<IncidentCaseV2Step> } | null)
        ?.completedSteps ?? []
  )
  const [currentStepIndex, setCurrentStepIndex] = useState(() =>
    landingStepIndex(completedSteps)
  )
  const [visitedThisSession, setVisitedThisSession] = useState<Set<number>>(
    () => new Set([currentStepIndex])
  )

  // Steps saved on an earlier visit count as reached.
  const visitedStepIndexes = useMemo(() => {
    const visited = new Set(visitedThisSession)
    INCIDENT_CASE_V2_STEPS.forEach((step, index) => {
      if (completedSteps.includes(step.id)) {
        visited.add(index)
      }
    })
    return visited
  }, [visitedThisSession, completedSteps])

  const currentStep = INCIDENT_CASE_V2_STEPS[currentStepIndex]
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === TOTAL_INCIDENT_CASE_V2_STEPS - 1

  // While the step differs from what was saved, leaving it asks first.
  const [isStepDirty, setIsStepDirty] = useState(false)
  const [pendingStepIndex, setPendingStepIndex] = useState<number | null>(null)

  const [isLeavePromptShown, confirmLeave, cancelLeave] = usePrompt(isStepDirty)

  // Exit waits for the step to be clean, so the leave prompt doesn't block it.
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    if (isExiting && !isStepDirty) {
      onExit()
    }
  }, [isExiting, isStepDirty, onExit])

  const goToStep = (index: number) => {
    setCurrentStepIndex(index)
    setIsStepDirty(false)
    setVisitedThisSession((visited) => new Set(visited).add(index))
  }

  const requestStep = (index: number) => {
    if (index !== currentStepIndex) {
      isStepDirty ? setPendingStepIndex(index) : goToStep(index)
    }
  }

  /**
   * Validate, save, then advance; a step not built yet moves on unsaved. A
   * second click while the first is in flight would open the case twice.
   */
  const handleNext = async () => {
    if (isSubmitting.current) {
      return
    }
    isSubmitting.current = true
    setIsBusy(true)
    const handle = handleRef.current

    try {
      if (currentStep.Component && !handle) {
        throw new Error(`Step ${currentStep.id} registered no handle`)
      }
      if (handle) {
        if (!(await handle.validate())) {
          return
        }
        const values = handle.getValues()
        if (!hasConfirmedLogUpdate.current && handle.changesLog()) {
          const isConfirmed = await new Promise<boolean>((resolve) =>
            setAnswerLogUpdate(() => resolve)
          )
          setAnswerLogUpdate(null)
          if (!isConfirmed) {
            return
          }
          hasConfirmedLogUpdate.current = true
        }
        if (handle.beforeSave && !(await handle.beforeSave())) {
          return
        }
        const result = await saveStep({
          isCaseOpen,
          step: currentStep.id,
          values
        })
        if (!isCaseOpen) {
          showToast({ title: t(incidentReportsConstants.CASE_CREATED) })
        }
        setIsCaseOpen(true)
        setCompletedSteps(result.completedSteps)
        // Files can only be attached to what the save has stored.
        const { isDone, savedValues } = handle.afterSave
          ? await handle.afterSave(values)
          : { isDone: true, savedValues: values }
        // Edits made while saving aren't saved yet: stay on the step with them.
        if (handle.markSaved(savedValues) || !isDone) {
          return
        }
      }
      if (isLastStep) {
        // A new case said so when it opened; an edited one says so here.
        if (isResuming) {
          showToast({ title: t(incidentReportsConstants.CASE_UPDATED) })
        }
        setIsExiting(true)
      } else {
        goToStep(currentStepIndex + 1)
      }
    } catch (error) {
      // Not an API failure, so a bug on our side rather than the connection.
      if (!isApiError(error)) {
        Sentry.captureException(error)
      }
      showToast({
        type: 'error',
        title: t(
          isAttachmentsStillStored(error)
            ? incidentReportsConstants.CASE_V2_ATTACHMENTS_SAVE_REFUSED
            : incidentReportsConstants.CASE_V2_SAVE_FAILED
        )
      })
    } finally {
      isSubmitting.current = false
      setIsBusy(false)
    }
  }

  const StepComponent = currentStep.Component

  return (
    <Page data-testid={`${testId}-wrapper`}>
      <Header data-testid={`${testId}-header`}>
        <PageHeader
          // Apart from the footer Back, which has `${testId}-back-button`.
          testId={`${testId}-page-header`}
          title={t(
            isResuming
              ? incidentReportsConstants.EDITING_INCIDENT_CASE
              : incidentReportsConstants.CREATING_INCIDENT_CASE
          )}
          onBack={onExit}
          // Where Back lands: the log until the case is open, then its preview.
          onBackTitle={t(
            isCaseOpen
              ? incidentReportsConstants.CASE_V2_BACK_TO_INCIDENT_PREVIEW
              : incidentReportsConstants.CASE_V2_BACK_TO_INCIDENT_LOG
          )}
        />
      </Header>

      <ConfigProvider theme={stepAntdTheme}>
        <Body data-testid={`${testId}-body`}>
          <Main>
            <Card padding="24" data-testid={`${testId}-step-card`}>
              <StepCardHeader data-testid={`${testId}-step-header`}>
                <CaseCardTitle
                  weight="medium"
                  data-testid={`${testId}-step-title`}
                >
                  {t(currentStep.titleKey)}
                </CaseCardTitle>
                {currentStep.subtitleKey && (
                  <CardSubheading data-testid={`${testId}-step-subtitle`}>
                    {t(currentStep.subtitleKey)}
                  </CardSubheading>
                )}
              </StepCardHeader>

              <StepCardContent
                data-testid={`${testId}-step-content-${currentStep.id}`}
              >
                {/* The asterisk is a glyph, not copy, so it is not in the key. */}
                <Text
                  tone="secondary"
                  align="end"
                  data-testid={`${testId}-required-hint`}
                >
                  {t(incidentReportsConstants.CASE_V2_REQUIRED_FIELDS_HINT)}{' '}
                  <Text as="span" tone="critical">
                    *
                  </Text>
                </Text>

                {StepComponent ? (
                  // Keyed so each step mounts fresh with its own handle.
                  <StepComponent
                    key={currentStep.id}
                    incidentReport={incidentReport}
                    handleRef={handleRef}
                    onDirtyChange={setIsStepDirty}
                  />
                ) : (
                  <Text
                    tone="secondary"
                    data-testid={`${testId}-step-placeholder`}
                  >
                    {t(incidentReportsConstants.CASE_V2_STEP_NOT_BUILT_YET)}
                  </Text>
                )}
              </StepCardContent>

              <StepCardFooter data-testid={`${testId}-footer`}>
                {isEditingCase && canCloseIncident && (
                  <CloseIncident
                    variant="secondary"
                    data-testid={`${testId}-close-incident-button`}
                    disabled={isBusy}
                    onClick={() => setIsClosingIncident(true)}
                  >
                    {t(incidentReportsConstants.CLOSE_INCIDENT)}
                  </CloseIncident>
                )}
                {isEditingCase && !canCloseIncident && (
                  // A disabled button fires no pointer or focus events, so
                  // the tooltip hangs off a focusable wrapper instead.
                  <Tooltip
                    title={t(
                      incidentReportsConstants.CASE_V2_CLOSE_INCIDENT_NOT_PERMITTED
                    )}
                    trigger={['hover', 'focus']}
                  >
                    <CloseIncidentNotPermitted
                      tabIndex={0}
                      aria-describedby={closeNotPermittedId}
                      data-testid={`${testId}-close-incident-not-permitted`}
                    >
                      <Button
                        variant="secondary"
                        data-testid={`${testId}-close-incident-button`}
                        disabled
                      >
                        {t(incidentReportsConstants.CLOSE_INCIDENT)}
                      </Button>
                      <span id={closeNotPermittedId} hidden>
                        {t(
                          incidentReportsConstants.CASE_V2_CLOSE_INCIDENT_NOT_PERMITTED
                        )}
                      </span>
                    </CloseIncidentNotPermitted>
                  </Tooltip>
                )}
                {!isFirstStep && (
                  <Button
                    variant="secondary"
                    data-testid={`${testId}-back-button`}
                    disabled={isBusy}
                    onClick={() => requestStep(currentStepIndex - 1)}
                  >
                    {t(incidentReportsConstants.CASE_V2_BACK)}
                  </Button>
                )}
                <Button
                  variant="primary"
                  data-testid={`${testId}-next-button`}
                  onClick={handleNext}
                  disabled={isBusy}
                >
                  {isLastStep
                    ? t(incidentReportsConstants.CASE_V2_FINISH)
                    : t(incidentReportsConstants.CASE_V2_NEXT)}
                </Button>
              </StepCardFooter>
            </Card>
          </Main>

          <Rail data-testid={`${testId}-rail`}>
            <ServiceUserDetailsCard
              testId={`${testId}-service-user`}
              residentId={incidentReport.serviceUser.id}
            />
            <IncidentCaseV2Stepper
              testId={`${testId}-stepper`}
              completedSteps={completedSteps}
              currentStepIndex={currentStepIndex}
              isDisabled={isBusy}
              visitedStepIndexes={visitedStepIndexes}
              onStepSelect={requestStep}
            />
          </Rail>
        </Body>
      </ConfigProvider>

      <LeavePrompt
        showPrompt={isLeavePromptShown}
        confirmNavigation={confirmLeave}
        cancelNavigation={cancelLeave}
        modalCancelText={tGeneral(generalConstants.CANCEL)}
        modalConfirmText={tGeneral(generalConstants.EXIT_WITHOUT_SAVING)}
        modalContentText={tGeneral(generalConstants.MODAL_CONTENT)}
        modalTitle={tGeneral(generalConstants.MODAL_TITLE)}
      />
      <LeavePrompt
        showPrompt={pendingStepIndex !== null}
        confirmNavigation={() => {
          if (pendingStepIndex !== null) {
            goToStep(pendingStepIndex)
          }
          setPendingStepIndex(null)
        }}
        cancelNavigation={() => setPendingStepIndex(null)}
        modalCancelText={tGeneral(generalConstants.CANCEL)}
        modalConfirmText={tGeneral(generalConstants.EXIT_WITHOUT_SAVING)}
        modalContentText={tGeneral(generalConstants.MODAL_CONTENT)}
        modalTitle={tGeneral(generalConstants.MODAL_TITLE)}
      />
      <LogUpdateConfirm
        open={answerLogUpdate !== null}
        onCancel={() => answerLogUpdate?.(false)}
        onContinue={() => answerLogUpdate?.(true)}
        testId={`${testId}-log-update`}
      />
      <CloseIncidentModal
        open={isClosingIncident}
        setIsClosingIncident={setIsClosingIncident}
        incidentReport={incidentReport}
        onSuccess={() => {
          // The incident is closed, so its unsaved step changes go with it.
          setIsStepDirty(false)
          setIsExiting(true)
        }}
      />
    </Page>
  )
}

const { color, spacing } = cssVars

/** The frame's content column (680 card, 24 gap, 328 rail); no layout token. */
const CONTENT_MAX_WIDTH = '1032px'

/** Centres the header and body; their padding sits outside the column. */
const Page = styled.div`
  box-sizing: border-box;
  max-width: calc(${CONTENT_MAX_WIDTH} + 2 * ${spacing[300]});
  margin-inline: auto;
`

/** Spans the content and rail, and scrolls away with the page. */
const Header = styled.div`
  padding: ${spacing[300]} ${spacing[300]} 0;

  @media ${STEP_COLLAPSE} {
    padding: ${spacing[200]} ${spacing[200]} 0;
  }
`

/** The rail is hidden when the page collapses, as designed. */
const Body = styled.div`
  display: flex;
  gap: ${spacing[300]};
  align-items: flex-start;
  padding: ${spacing[300]};

  @media ${STEP_COLLAPSE} {
    flex-direction: column;
    padding: ${spacing[200]};
  }
`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[200]};
  flex: 1;
  min-width: 0;
`

/** 328px is the frame's rail width; there is no token for a layout measure. */
const Rail = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${spacing[200]};
  width: 328px;
  flex-shrink: 0;

  @media ${STEP_COLLAPSE} {
    display: none;
  }
`

/** The frame's 16px header and 24px body, ruled off across the card. */
const StepCardHeader = styled(CardHeader)`
  border-bottom: 1px solid ${color.border.subtle};
  padding-block: ${spacing[200]};
`

const StepCardContent = styled(CardContent)`
  padding-block: ${spacing[300]};
`

const StepCardFooter = styled(CardFooter)`
  border-top: 1px solid ${color.border.subtle};
`

/** Close incident sits on the left of the footer, apart from Back and Next. */
const CloseIncident = styled(Button)`
  margin-right: auto;
`

const CloseIncidentNotPermitted = styled.span`
  margin-right: auto;

  /* Lets the wrapper, not the disabled button, receive the hover. */
  & > button {
    pointer-events: none;
  }
`

export default IncidentCaseV2
